import { durationLabel, jobTimesFacts, type FirstClean, type TimedVisit } from './job-times'
import { noFixedCycle } from './planning'
import { shortMoney } from './month-view'
import { currencySymbol } from './round-config'
import type { Customer } from './types'
import { dayLabel, el, money } from './ui'

// Tier and job times, at the top of a customer's Analysis, as a game's player card (Liam, 13 Oct 2026: "almost like
// circle in games ie tier one like a stats breakdown", then of the designs: "I like the B player card", "the tier
// ladder is useful addition", "Yes i approve the new design thats perfect"). A slate card: their tier and pence a
// minute, the round's tiers as a row of circles with theirs lit lemon, six stat squares (usual time, fronts, backs,
// insides, price, how often), who does the job and how long each takes, and the last timed job. Their tier, pence a
// minute and how long each part takes come from the server's tier figures (functions/lib/tiers.ts); the usual time and
// each worker's time are worked out here from their visits (jobTimesFacts in job-times.ts). The tiers row needs the
// round's cut-offs (Extra features); with tiers off it is left out.

const jobsWord = (n: number): string => `${n} ${n === 1 ? 'job' : 'jobs'}`

// "75p" under a pound on a UK round, "£1.50" or "$0.75" otherwise.
function rate(pence: number): string {
  return currencySymbol() === '£' && pence < 100 ? `${pence}p` : shortMoney(pence)
}

function tierNumber(c: Customer, tiers: number): number | null {
  const n = Number(String(c.tier ?? '').replace(/^tier\s*/i, '').trim())
  return Number.isInteger(n) && n >= 1 && n <= Math.max(tiers, 9) ? n : null
}

// "2p over the Tier 1 line", "8p short of Tier 2", or the owner's own choice.
function standing(c: Customer, n: number | null, cutoffs: number[] | null): string | null {
  if (n !== null && c.tier_by_hand) return `you chose Tier ${n}`
  const r = c.tier_rate_pence
  if (n === null || typeof r !== 'number' || !cutoffs) return null
  if (n === 1) return r === cutoffs[0] ? 'right on the Tier 1 line' : `${rate(r - cutoffs[0]!)} over the Tier 1 line`
  const above = cutoffs[n - 2]
  return typeof above === 'number' && above > r ? `${rate(above - r)} short of Tier ${n - 1}` : null
}

function stat(value: string, label: string, unit?: string): HTMLElement {
  const big = el('span', { class: `tier-stat-value${!unit && value.length > 5 ? ' tier-stat-long' : ''}` }, value)
  if (unit) big.append(el('span', { class: 'tier-stat-unit' }, ` ${unit}`))
  return el('div', { class: 'tier-stat' }, big, el('span', { class: 'tier-stat-label' }, label))
}

function minutesStat(minutes: number | null | undefined, label: string): HTMLElement {
  if (typeof minutes !== 'number') return stat('–', label)
  return minutes < 60 ? stat(String(minutes), label, 'min') : stat(durationLabel(minutes), label)
}

function everyWords(days: number | null): { value: string; unit?: string } {
  if (!days) return { value: 'Not set' }
  return days % 7 === 0 ? { value: String(days / 7), unit: days === 7 ? 'wk' : 'wks' } : { value: String(days), unit: 'days' }
}

export function jobTimesCard(c: Customer, visits: TimedVisit[], cutoffs: number[] | null, firstClean: FirstClean | null = null): HTMLElement {
  const f = jobTimesFacts(visits, firstClean)
  const n = tierNumber(c, cutoffs ? cutoffs.length + 1 : 4)
  const card = el('section', { class: 'tier-card', 'aria-label': 'Tier and job times' })
  const head = el('div', { class: 'tier-card-head' },
    el('span', { class: 'tier-card-kicker' }, n !== null ? `Tier ${n} customer` : 'No tier yet'),
    el('strong', { class: 'tier-card-name' }, c.name))
  const words = [typeof c.tier_rate_pence === 'number' ? `${money(c.tier_rate_pence)} a minute` : null, standing(c, n, cutoffs)].filter((w): w is string => !!w)
  if (words.length) head.append(el('span', { class: 'tier-card-rate' }, words.join(' · ')))
  card.append(head)
  if (cutoffs) {
    const ladder = el('ol', { class: 'tier-ladder', 'aria-label': n !== null ? `Tier ${n} of ${cutoffs.length + 1}` : `${cutoffs.length + 1} tiers` })
    for (let i = 1; i <= cutoffs.length + 1; i++) {
      const line = i <= cutoffs.length ? `${rate(cutoffs[i - 1]!)}+` : `under ${rate(cutoffs.at(-1)!)}`
      ladder.append(el('li', { class: `tier-step${i === n ? ' tier-step-on' : ''}` }, el('span', { class: 'tier-step-badge' }, String(i)), el('span', { class: 'tier-step-line' }, line)))
    }
    card.append(ladder)
  }
  if (!f.timed) {
    card.append(el('p', { class: 'tier-card-empty' }, 'No job with Start and Finish tapped yet. Times show here after the first one.'))
    return card
  }
  const every = everyWords(c.cycle_days)
  // Each time named by the kind of clean it is (Liam, 21 Oct 2026: "should it say usual or adhoc?"): Regular for a
  // customer on a cycle, Ad hoc for one who asks when they want one (When they ask, One off), whose every clean counts.
  // Only tiles with a figure, the time, the price and how often always ("if we have cycle prices and adhoc times should
  // there be more tiles": yes, "go ahead with that change").
  const kind = noFixedCycle(c) ? 'Ad hoc' : 'Regular'
  // The first clean's price in a tile of its own when it differs ("First clean · £45" read as one thing: Astra).
  const firstPrice = typeof c.first_clean_price_pence === 'number' && c.first_clean_price_pence !== c.reg_price_pence ? c.first_clean_price_pence : null
  const tiles: HTMLElement[] = []
  let why: HTMLElement | null = null
  if (f.usualFromFirst && f.first) {
    // Only the first clean timed so far (Liam: "this can go in there however you can hover over to explain the data maybe
    // its a different color to highlight warning tho not too strong"): its tile in the time's place, soft amber, and a
    // hover (computer) or a tap (phone) says why.
    const tile = minutesStat(f.first.minutes, 'First clean')
    const words = `Only the first clean is timed (${dayLabel(f.first.date)}). ${kind} time not known yet.`
    why = el('p', { class: 'tier-card-why', id: `first-why-${c.id}` }, words)
    why.hidden = true
    tile.classList.add('tier-stat-first')
    tile.setAttribute('title', words)
    tile.setAttribute('role', 'button')
    tile.setAttribute('tabindex', '0')
    tile.setAttribute('aria-expanded', 'false')
    tile.setAttribute('aria-controls', why.id)
    tile.setAttribute('aria-describedby', why.id)
    tile.querySelector('.tier-stat-label')?.append(el('span', { class: 'tier-stat-mark', 'aria-hidden': 'true' }, ' ⓘ'))
    const toggle = (): void => { why!.hidden = !why!.hidden; tile.setAttribute('aria-expanded', String(!why!.hidden)) }
    tile.addEventListener('click', toggle)
    tile.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle() } })
    tiles.push(tile)
  } else {
    const time = minutesStat(f.usual, f.usualWithInsides ? `${kind}, insides` : kind)
    // What it is worked out from, on hover (Astra: earlier visits may have been different).
    time.setAttribute('title', kind === 'Ad hoc'
      ? 'Their last three timed cleans, the first clean included: the next one will be like it.'
      : `Their last three timed cleans${f.first ? ', not the first clean' : ''}. Earlier visits may have been different.`)
    tiles.push(time)
    // Their first clean beside it (0082): left out of Regular, so its time is still seen; with a part still to do, only
    // what was done, said so (never one part as the whole).
    // Not when it is all an ad hoc customer's time is from (the same minutes twice: Sonnet; by which cleans, not by equal
    // minutes: Astra's final review).
    if (f.first && !(firstClean?.counts && f.usualOnlyFirst)) tiles.push(minutesStat(f.first.minutes, f.first.whole ? 'First clean' : `First clean, ${f.first.parts.map((p) => p.part).join(' and ')} so far`))
  }
  if (typeof c.fronts_minutes === 'number') tiles.push(minutesStat(c.fronts_minutes, 'Fronts'))
  if (typeof c.backs_minutes === 'number') tiles.push(minutesStat(c.backs_minutes, 'Backs'))
  if (typeof c.inside_minutes === 'number') tiles.push(minutesStat(c.inside_minutes, 'Insides'))
  // The prices: the regular one, and the first clean's when it differs. How often: their own answer when it is not a
  // cycle in days ("When they ask", "One off"), else "Not set" (Astra).
  tiles.push(stat(typeof c.reg_price_pence === 'number' ? shortMoney(c.reg_price_pence) : '–', kind === 'Ad hoc' ? 'A visit' : 'Regular price'))
  if (firstPrice !== null) tiles.push(stat(shortMoney(firstPrice), 'First clean price'))
  const answer = (c.cycle_text ?? '').trim()
  tiles.push(!c.cycle_days && answer ? stat(answer.charAt(0).toUpperCase() + answer.slice(1), 'How often') : stat(every.value, 'Every', every.unit))
  // Two across on a phone, the last one the whole width when it is alone; on a computer three across, four two by two.
  card.append(el('div', { class: `tier-stats${tiles.length === 4 ? ' tier-stats-two' : ''}` }, ...tiles))
  if (why) card.append(why)
  if (f.byWorker.length) {
    const quickest = f.byWorker.length > 1 ? Math.min(...f.byWorker.map((w) => w.minutes)) : null
    const who = el('div', { class: 'tier-who' }, el('span', { class: 'tier-card-kicker tier-who-title' }, 'Who does it'))
    f.byWorker.forEach((w, i) => {
      const row = el('div', { class: 'tier-who-row', 'aria-label': `${w.name}: ${durationLabel(w.minutes)} on average over ${jobsWord(w.jobs)}` },
        el('span', { class: `tier-who-initial${(quickest === null ? i === 0 : w.minutes === quickest) ? ' tier-who-lead' : ''}`, 'aria-hidden': 'true' }, w.name.charAt(0).toUpperCase()),
        el('span', { class: 'tier-who-name' }, w.name),
        el('span', { class: 'tier-who-time' }, durationLabel(w.minutes)))
      if (quickest !== null && w.minutes === quickest) row.append(el('span', { class: 'tier-who-quickest' }, 'Quickest'))
      who.append(row)
    })
    card.append(who)
  }
  if (f.last) {
    card.append(el('p', { class: 'tier-card-foot' }, `Last timed ${dayLabel(f.last.date)}, ${durationLabel(f.last.minutes)}${f.last.part ? `, ${f.last.part} only` : ''} · ${f.timed} timed ${f.timed === 1 ? 'job' : 'jobs'}`))
  }
  return card
}
